import { useState } from "react";
import { Logo } from "../../components/dashboard/Logo";
import { useConnectIntegration } from "../../hooks/useConnectIntegration";
import {
  isAppGroupToken,
  useAppChoiceGroups,
  useIntegrationsBySlug,
  type AppChoiceGroup,
  type CatalogIntegration,
} from "../../hooks/useIntegrationsCatalog";
import type { Integration } from "../../types/integration";
import styles from "./OnboardingWizard.module.css";

// One pinned requirement resolved for display: a single app, or a choice
// between providers ("connect your email").
type ResolvedApp = {
  token: string;
  label: string;
  group: AppChoiceGroup | undefined;
  options: ReadonlyArray<CatalogIntegration>;
};

export type ServableApps = {
  visible: ReadonlyArray<ResolvedApp>;
};

// Callers gate their surrounding copy on `visible`, not on the raw token list:
// a heading like "Next: connect your apps" with nothing under it is worse than
// staying quiet. Filtering only kicks in once the catalog lookup has answered
// — on "loading"/"error" we keep what we were given, so a slow or failed
// request never silently swallows the whole prompt.
export const useServableApps = (tokens: ReadonlyArray<string>): ServableApps => {
  const { groups } = useAppChoiceGroups();
  const groupById = new Map(groups.map((g) => [g.id, g]));
  const slugTokens = tokens.filter((t) => !isAppGroupToken(t));
  const { bySlug, status } = useIntegrationsBySlug(slugTokens);

  const visible = tokens.flatMap((token): ResolvedApp[] => {
    if (isAppGroupToken(token)) {
      const group = groupById.get(token);
      if (group === undefined || group.members.length === 0) return [];
      return [{ token, label: group.prompt, group, options: group.members }];
    }
    const entry = bySlug.get(token);
    if (entry === undefined) {
      // Unresolved: drop it only once we know the catalog's answer.
      return status === "ready" ? [] : [{ token, label: token, group: undefined, options: [] }];
    }
    return [{ token, label: entry.title, group: undefined, options: [entry] }];
  });

  return { visible };
};

// Read-only roll-call for the Review step. Nothing is connectable yet (the
// instance doesn't exist until submit), so this only sets the expectation.
export const RequestedAppsSummary = ({ visible }: ServableApps): JSX.Element => (
  <div className={styles.appSummary}>
    {visible.map((app) => (
      <span key={app.token} className={styles.appSummaryChip} data-testid="review-app-chip">
        {app.group === undefined ? (
          <Logo
            slug={app.token}
            size={16}
            brandHex={app.options[0]?.iconBrandHex ?? null}
            logoUrl={app.options[0]?.ourLogoUrl ?? app.options[0]?.logoUrl ?? null}
          />
        ) : null}
        {app.group === undefined ? app.label : app.group.title}
      </span>
    ))}
  </div>
);

type LinksProps = ServableApps & {
  // The tenant the connect runs against; null before one exists, in which case
  // we can only hand the customer to the integration's page.
  tenantId: string | null;
};

// Each option starts its connect in a NEW tab — OAuth directly where we can,
// the integration's own page otherwise. A customer working through three apps
// must not lose this list to a same-tab navigation.
export const RequestedAppsLinks = ({ visible, tenantId }: LinksProps): JSX.Element => {
  const startConnect = useConnectIntegration(tenantId);
  const [busy, setBusy] = useState<string | null>(null);

  const connect = async (slug: string, entry: CatalogIntegration | undefined): Promise<void> => {
    setBusy(slug);
    try {
      // The catalog DTO carries the fields the connect decision needs; the
      // per-tenant Integration is a superset of them.
      await startConnect(slug, entry as unknown as Integration | undefined);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={styles.appList}>
      {visible.map((app) => {
        // A token the catalog hasn't answered for yet still gets one button:
        // `useConnectIntegration` sends an unknown integration to its own
        // detail page in a new tab, which is where the pre-IP-588 link went.
        // Without it the row is a card with nothing to click — for the length
        // of the round trip on every visit, and permanently when the lookup
        // errors, since that settles on "error" rather than "ready".
        const actions =
          app.options.length > 0
            ? app.options.map((option) => ({
                slug: option.slug,
                title: option.title,
                entry: option as CatalogIntegration | undefined,
              }))
            : [{ slug: app.token, title: app.token, entry: undefined }];

        return (
          <div key={app.token} className={styles.appCard}>
            {app.group === undefined ? (
              <Logo
                slug={app.token}
                size={32}
                brandHex={app.options[0]?.iconBrandHex ?? null}
                logoUrl={app.options[0]?.ourLogoUrl ?? app.options[0]?.logoUrl ?? null}
              />
            ) : (
              <span className={styles.appCardGroupMark} aria-hidden="true">✉</span>
            )}
            <span className={styles.appCardTitle}>{app.label}</span>
            <span className={styles.appCardActions}>
              {actions.map((action) => (
                <button
                  key={action.slug}
                  type="button"
                  className={styles.appCardCta}
                  disabled={busy === action.slug}
                  aria-label={`Connect ${action.title}`}
                  onClick={() => void connect(action.slug, action.entry)}
                >
                  {busy === action.slug
                    ? "Opening…"
                    : actions.length > 1
                      ? action.title
                      : "Connect →"}
                </button>
              ))}
            </span>
          </div>
        );
      })}
    </div>
  );
};
